import { Quote } from 'lucide-react';

/**
 * Customer testimonials.
 *
 * ⚠️ LEGAL/TRUST: do NOT add fabricated quotes or invented names/companies.
 * Only populate `TESTIMONIALS` with real, consented quotes. Until then this
 * array stays empty and the section renders nothing (returns null) — shipping
 * an empty section is correct; shipping fake social proof is not.
 *
 * To enable: add entries with the customer's real attribution (and written
 * permission on file). Use a generic role (e.g. "Owner, dental practice") only
 * if the customer consents to anonymized attribution.
 */
export interface Testimonial {
  quote: string;
  /** Real attribution, e.g. "Owner, Bright Smile Dental". */
  attribution: string;
}

export const TESTIMONIALS: Testimonial[] = [];

export function Testimonials({
  items = TESTIMONIALS,
}: {
  items?: Testimonial[];
}): JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          What customers say
        </h2>
      </div>
      <div className="mx-auto mt-12 grid max-w-4xl gap-6 sm:grid-cols-2">
        {items.map((t) => (
          <figure
            key={t.attribution}
            className="rounded-xl border border-border bg-card p-6 shadow-sm"
          >
            <Quote className="h-5 w-5 text-primary" aria-hidden="true" />
            <blockquote className="mt-3 text-sm leading-relaxed">{t.quote}</blockquote>
            <figcaption className="mt-4 text-sm font-medium text-muted-foreground">
              {t.attribution}
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
